/**
 * Стороны указателя (docs/wayfinding/plan.md §9.1.4).
 *
 * У носителя с двумя и более сторонами макет у каждой стороны свой: свои данные генератора
 * Автокрафта (или свой загруженный PDF), своя обложка, своя страница. Число сторон задаёт
 * ТИП указателя, а сами макеты лежат в свойствах точки.
 *
 * Сторона 1 — это прежние ключи макета как есть: указатели, заполненные до появления
 * сторон, остаются заполненными без миграции. Остальные стороны — те же ключи под
 * префиксом `__side<N>:`. Плоские ключи, а не вложенный объект: слияние свойств на сервере
 * идёт по ключам верхнего уровня, и две записи подряд (данные формы, затем обложка из
 * ответа Автокрафта) иначе собирали бы объект стороны из устаревшего снимка и затирали
 * друг друга. Двойное подчёркивание прячет их из карточки, таблицы и списков полей.
 */
import {
  AUTOCRAFT_COVER_PROPERTY,
  AUTOCRAFT_COVER_SIGNATURE_PROPERTY,
  AUTOCRAFT_DATA_PROPERTY,
  AUTOCRAFT_GENERATOR_PROPERTY,
  AUTOCRAFT_PAGE_COUNT_PROPERTY,
  AUTOCRAFT_PAGE_PROPERTY,
  AUTOCRAFT_PDF_PROPERTY,
} from '../types/autocraft.js';
import { wayfindingSignPreviewPlacements } from './wayfindingSignPreviewDirections.js';
import {
  WAYFINDING_SIGN_LAYOUT_PROPERTY,
  WAYFINDING_SIGN_MAX_SIDES,
  WAYFINDING_SIGN_PREVIEWS_PROPERTY,
  readWayfindingSignLayout,
  type WayfindingSignType,
} from '../types/wayfinding.js';
import {
  readWayfindingSignPreviewAnchor,
  readWayfindingSignPreviewAnchorByPage,
  resolveWayfindingSignTypeId,
} from './wayfindingSignTypes.js';

/**
 * Ключи макета, которые у каждой стороны свои.
 *
 * Отпечаток обложки — тоже: он про обложку СТОРОНЫ. Без него в списке форма второй стороны
 * читала и писала отпечаток первой (сервер же писал его в ключ стороны), и открытие каждой
 * стороны признавало обложку соседней устаревшей — пересборки шли по кругу.
 */
export const WAYFINDING_SIGN_SIDE_KEYS: readonly string[] = [
  AUTOCRAFT_GENERATOR_PROPERTY,
  AUTOCRAFT_DATA_PROPERTY,
  AUTOCRAFT_COVER_PROPERTY,
  AUTOCRAFT_COVER_SIGNATURE_PROPERTY,
  AUTOCRAFT_PDF_PROPERTY,
  AUTOCRAFT_PAGE_PROPERTY,
  AUTOCRAFT_PAGE_COUNT_PROPERTY,
  WAYFINDING_SIGN_LAYOUT_PROPERTY,
];

const SIDE_KEYS: ReadonlySet<string> = new Set(WAYFINDING_SIGN_SIDE_KEYS);

/** Номер стороны, пригодный для ключа: целое 1…`WAYFINDING_SIGN_MAX_SIDES`. */
export function clampWayfindingSignSide(side: number): number {
  if (!Number.isFinite(side)) {
    return 1;
  }
  return Math.max(1, Math.min(WAYFINDING_SIGN_MAX_SIDES, Math.floor(side)));
}

/** Ключ свойства стороны: у первой — сам ключ, у остальных — `__side<N>:<ключ>`. */
export function wayfindingSignSideKey(key: string, side: number): string {
  const n = clampWayfindingSignSide(side);
  return n === 1 ? key : `__side${n}:${key}`;
}

/** Сколько сторон у указателя этого типа; без типа — одна. */
export function wayfindingSignSideCount(type: Pick<WayfindingSignType, 'sides'> | null | undefined): number {
  return clampWayfindingSignSide(type?.sides ?? 1);
}

/**
 * Свойства точки глазами одной стороны: ключи макета этой стороны лежат под обычными
 * именами. Так блоки макета (форма Автокрафта, свой PDF) работают со стороной, ничего не
 * зная о сторонах. У первой стороны — те же свойства, без копии.
 */
export function readWayfindingSignSideProperties(
  properties: Record<string, unknown>,
  side: number,
): Record<string, unknown> {
  const n = clampWayfindingSignSide(side);
  if (n === 1) {
    return properties;
  }
  const view: Record<string, unknown> = { ...properties };
  for (const key of WAYFINDING_SIGN_SIDE_KEYS) {
    const sideKey = wayfindingSignSideKey(key, n);
    if (sideKey in properties) {
      view[key] = properties[sideKey];
    } else {
      delete view[key];
    }
  }
  return view;
}

/** Обратное к `readWayfindingSignSideProperties`: правку стороны — в её собственные ключи. */
export function toWayfindingSignSidePatch(
  patch: Record<string, unknown>,
  side: number,
): Record<string, unknown> {
  const n = clampWayfindingSignSide(side);
  if (n === 1) {
    return patch;
  }
  return Object.fromEntries(
    Object.entries(patch).map(([key, value]) => [
      SIDE_KEYS.has(key) ? wayfindingSignSideKey(key, n) : key,
      value,
    ]),
  );
}

/**
 * Превью макета стороны на карте; `null` — макета нет.
 *
 * У типа с генератором — обложка Автокрафта, но только собранная ЭТИМ генератором: данные
 * прежнего генератора (тип сменили) к макету уже не относятся, их и форма стороны признаёт
 * чужими. У типа без генератора — первая страница своего PDF.
 */
export function wayfindingSignSidePreviewUrl(
  properties: Record<string, unknown>,
  side: number,
  type: Pick<WayfindingSignType, 'autocraftGeneratorHash'> | null | undefined,
): string | null {
  const view = readWayfindingSignSideProperties(properties, side);
  if (type?.autocraftGeneratorHash) {
    if (view[AUTOCRAFT_GENERATOR_PROPERTY] !== type.autocraftGeneratorHash) {
      return null;
    }
    const cover = view[AUTOCRAFT_COVER_PROPERTY];
    return typeof cover === 'string' && cover !== '' ? cover : null;
  }
  return readWayfindingSignLayout(view)?.previewUrls[0] ?? null;
}

/** Превью всех сторон указателя по порядку сторон; стороны без макета пропускаются. */
export function wayfindingSignPreviewUrls(
  properties: Record<string, unknown> | null | undefined,
  type: Pick<WayfindingSignType, 'sides' | 'autocraftGeneratorHash'> | null | undefined,
): string[] {
  const props = properties ?? {};
  const out: string[] = [];
  const sides = wayfindingSignSideCount(type);
  for (let side = 1; side <= sides; side += 1) {
    const url = wayfindingSignSidePreviewUrl(props, side, type);
    if (url) {
      out.push(url);
    }
  }
  return out;
}

/** Показанная страница макета стороны (выбор человека в панели); 1 — по умолчанию. */
export function wayfindingSignSidePage(
  properties: Record<string, unknown> | null | undefined,
  side: number,
): number {
  const view = readWayfindingSignSideProperties(properties ?? {}, side);
  const raw = view[AUTOCRAFT_PAGE_PROPERTY];
  const page = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(page) && page >= 1 ? Math.floor(page) : 1;
}

/**
 * Сколько страниц в самом «толстом» собранном макете указателя — по всем сторонам (у фингерпоста
 * макет привязан к стороне). Число пишет сервер после сборки превью; 0 — ни одной сборки.
 */
export function wayfindingSignBuiltPageCount(properties: Record<string, unknown> | null | undefined): number {
  let max = 0;
  for (let side = 1; side <= WAYFINDING_SIGN_MAX_SIDES; side += 1) {
    const raw = properties?.[wayfindingSignSideKey(AUTOCRAFT_PAGE_COUNT_PROPERTY, side)];
    const count = typeof raw === 'number' ? raw : Number(raw);
    if (Number.isFinite(count) && count > max) {
      max = Math.floor(count);
    }
  }
  return max;
}

/**
 * Страница макета, которую стороне задаёт ТИП (`autocraftPageBySide`); `null` — тип страницу
 * не задаёт: у типа нет генератора, сторона одна или разводка по страницам не настроена.
 * Разводка настроена — сторона без своей записи стоит на первой странице.
 */
export function wayfindingSignTypeSidePage(
  type: Pick<WayfindingSignType, 'sides' | 'autocraftGeneratorHash' | 'autocraftPageBySide'> | null | undefined,
  side: number,
): number | null {
  if (!type?.autocraftGeneratorHash || !type.autocraftPageBySide || wayfindingSignSideCount(type) < 2) {
    return null;
  }
  return type.autocraftPageBySide[String(clampWayfindingSignSide(side))] ?? 1;
}

/**
 * Страница, на которой собирается макет стороны: заданная типом, иначе сохранённая у стороны.
 * Одна на панель, пересборку всех макетов, таблицу и выгрузку — иначе они разошлись бы.
 */
export function wayfindingSignSideLayoutPage(
  properties: Record<string, unknown> | null | undefined,
  side: number,
  type: Pick<WayfindingSignType, 'sides' | 'autocraftGeneratorHash' | 'autocraftPageBySide'> | null | undefined,
): number {
  return wayfindingSignTypeSidePage(type, side) ?? wayfindingSignSidePage(properties, side);
}

/**
 * Превью одной стороны на карте: картинка и НАПРАВЛЕНИЕ от точки, куда она встаёт
 * (`wayfindingSignPreviewDirections`).
 */
export interface WayfindingSignPreviewEntry {
  url: string;
  /** Градусы от «вверх» по часовой; `null` — превью по центру точки. */
  angleDeg: number | null;
  /** Поворот самой картинки, градусы по часовой. */
  rotationDeg: number;
  /** Точка превью, стоящая в точке знака: доли ширины и высоты. */
  anchor: readonly [number, number];
}

/**
 * Тип с якорями превью, которые действуют для ЭТОГО указателя: свой якорь указателя важнее якоря
 * слоя, якорь слоя — якоря типа, а типа нет — умолчания по сторонам. Ничего не переопределено —
 * тип как есть. У указателя без типа переопределение тоже действует: он односторонний.
 *
 * Якоря страниц макета (`previewAnchorByPage`) склеиваются постранично по той же цепочке:
 * указатель → слой → тип. Якорь страницы на любом уровне по-прежнему старше общего — он про
 * конкретную страницу генератора, а не про знак: свой общий якорь указателя страницу с якорем
 * слоя или типа не сдвинет, для неё у указателя свой якорь страницы.
 */
function withPreviewAnchorOverride(
  type: WayfindingSignPreviewType | null | undefined,
  properties: Record<string, unknown>,
  layerAnchor: string | null | undefined,
  layerAnchorByPage: Readonly<Record<string, string>> | null | undefined,
): WayfindingSignPreviewType | null | undefined {
  const anchor = readWayfindingSignPreviewAnchor(properties) ?? layerAnchor ?? null;
  const signAnchorByPage = readWayfindingSignPreviewAnchorByPage(properties);
  const anchorChanged = !!anchor && anchor !== type?.previewAnchor;
  if (!anchorChanged && !signAnchorByPage && !layerAnchorByPage) {
    return type;
  }
  const previewAnchorByPage =
    signAnchorByPage || layerAnchorByPage
      ? { ...type?.previewAnchorByPage, ...layerAnchorByPage, ...signAnchorByPage }
      : undefined;
  return {
    sides: 1,
    ...type,
    ...(anchorChanged ? { previewAnchor: anchor } : {}),
    ...(previewAnchorByPage ? { previewAnchorByPage } : {}),
  };
}

/**
 * Превью сторон указателя с направлениями: что и с какой стороны от знака стоит.
 * Стороны без макета пропускаются, но направления считаются по ВСЕМ сторонам носителя —
 * иначе у двустороннего знака с одним заполненным макетом превью уехало бы не на свою
 * сторону.
 *
 * @param layerAnchor — свой якорь слоя (`readWayfindingLayerPreviewAnchor`); свой якорь самого
 *   указателя (`WAYFINDING_SIGN_PREVIEW_ANCHOR_PROPERTY`) важнее него, оба важнее якоря типа.
 * @param layerAnchorByPage — свои якоря страниц слоя (`readWayfindingLayerPreviewAnchorByPage`):
 *   так же между якорями страниц указателя и типа.
 */
export function wayfindingSignPreviewEntries(
  properties: Record<string, unknown> | null | undefined,
  type: WayfindingSignPreviewType | null | undefined,
  layerAnchor?: string | null,
  layerAnchorByPage?: Readonly<Record<string, string>> | null,
): WayfindingSignPreviewEntry[] {
  const props = properties ?? {};
  const effectiveType = withPreviewAnchorOverride(type, props, layerAnchor, layerAnchorByPage);
  const sides = wayfindingSignSideCount(effectiveType);
  // Страницы всех сторон: у страницы бывает свой якорь, и раскладка считается с ним.
  const pages = Array.from({ length: sides }, (_, index) =>
    wayfindingSignSidePage(props, index + 1),
  );
  const urls = Array.from({ length: sides }, (_, index) =>
    wayfindingSignSidePreviewUrl(props, index + 1, effectiveType),
  );
  // Какие стороны нарисованы — чтобы пустая сторона не сгоняла видимую с якоря её страницы.
  const placements = wayfindingSignPreviewPlacements(
    effectiveType,
    pages,
    urls.map((url) => url !== null),
  );
  const out: WayfindingSignPreviewEntry[] = [];
  for (let side = 1; side <= sides; side += 1) {
    const url = urls[side - 1];
    if (!url) {
      continue;
    }
    const placement = placements[side - 1];
    out.push({
      url,
      angleDeg: placement?.angleDeg ?? null,
      rotationDeg: placement?.rotationDeg ?? 0,
      anchor: placement?.anchor ?? [0.5, 0.5],
    });
  }
  return out;
}

/**
 * Строка свойства карты: `угол,поворот,якорьX,якорьY\tссылка`, записи через перевод строки
 * (ни того, ни другого в адресах не бывает). Раскладка едет в ДАННЫХ, а не выражением
 * слоя: превью одной точки уходят в разные стороны, а `icon-anchor` у символа один.
 */
export function formatWayfindingSignPreviews(entries: readonly WayfindingSignPreviewEntry[]): string {
  return entries
    .map((entry) =>
      [
        `${entry.angleDeg === null ? '' : entry.angleDeg}`,
        entry.rotationDeg,
        entry.anchor[0],
        entry.anchor[1],
      ].join(',') + `\t${entry.url}`,
    )
    .join('\n');
}

/** Обратный разбор — им пользуется синк карты, собирая иконку раскладки. */
export function parseWayfindingSignPreviews(value: unknown): WayfindingSignPreviewEntry[] {
  if (typeof value !== 'string' || value === '') {
    return [];
  }
  return value.split('\n').flatMap((line): WayfindingSignPreviewEntry[] => {
    const tab = line.indexOf('\t');
    // Строка без раскладки — старое значение свойства (или ручная правка): превью по центру.
    const head = tab === -1 ? '' : line.slice(0, tab);
    const url = tab === -1 ? line : line.slice(tab + 1);
    if (url === '') {
      return [];
    }
    const [angleRaw, rotationRaw, anchorXRaw, anchorYRaw] = head.split(',');
    const number = (raw: string | undefined, fallback: number): number => {
      const parsed = Number(raw);
      return raw !== undefined && raw !== '' && Number.isFinite(parsed) ? parsed : fallback;
    };
    const angle = Number(angleRaw);
    return [
      {
        url,
        angleDeg: angleRaw !== undefined && angleRaw !== '' && Number.isFinite(angle) ? angle : null,
        rotationDeg: number(rotationRaw, 0),
        anchor: [number(anchorXRaw, 0.5), number(anchorYRaw, 0.5)],
      },
    ];
  });
}

interface SignFeatureLike {
  geometry?: { type?: string } | null;
  properties?: Record<string, unknown> | null;
}

/**
 * Данные слоя указателей ДЛЯ КАРТЫ: у каждой точки — превью макетов её сторон в
 * `WAYFINDING_SIGN_PREVIEWS_PROPERTY`, их рисует блок превью слоя. Копия, а не правка:
 * в базу превью не пишется, а смена генератора или числа сторон у типа видна сразу.
 *
 * Ничего не поменялось — возвращается та же коллекция: синк карты сравнивает данные по
 * ссылке, и новая копия на каждый рендер гоняла бы `setData` впустую.
 *
 * `layerSignTypeId` — тип носителя слоя: точка без своего типа считает превью по нему.
 * `layerPreviewAnchor` — свой якорь превью слоя (`readWayfindingLayerPreviewAnchor`): он важнее
 * якоря типа, а свой якорь точки важнее него. `layerPreviewAnchorByPage` — так же для якорей
 * страниц макета (`readWayfindingLayerPreviewAnchorByPage`).
 */
export function withWayfindingSignPreviews<C extends { features: readonly SignFeatureLike[] }>(
  collection: C,
  typeById: ReadonlyMap<string, WayfindingSignPreviewType>,
  layerSignTypeId: string | null = null,
  layerPreviewAnchor: string | null = null,
  layerPreviewAnchorByPage: Readonly<Record<string, string>> | null = null,
): C {
  let changed = false;
  const features = collection.features.map((feature) => {
    if (feature.geometry?.type !== 'Point') {
      return feature;
    }
    const typeId = resolveWayfindingSignTypeId(feature.properties, layerSignTypeId, typeById);
    const type = typeId ? typeById.get(typeId) : undefined;
    const previews = formatWayfindingSignPreviews(
      wayfindingSignPreviewEntries(feature.properties, type, layerPreviewAnchor, layerPreviewAnchorByPage),
    );
    if ((feature.properties?.[WAYFINDING_SIGN_PREVIEWS_PROPERTY] ?? '') === previews) {
      return feature;
    }
    changed = true;
    return {
      ...feature,
      properties: { ...feature.properties, [WAYFINDING_SIGN_PREVIEWS_PROPERTY]: previews },
    };
  });
  return changed ? { ...collection, features } : collection;
}

/** Что из типа нужно превью: стороны, генератор, форма знака и якоря. */
export type WayfindingSignPreviewType = Pick<
  WayfindingSignType,
  'sides' | 'autocraftGeneratorHash' | 'icon' | 'previewAnchor' | 'previewAnchorByPage'
>;

/**
 * Отпечаток справочника для превью: из типа их касается не всё — стороны, генератор,
 * иконка (по ней фигура знака) и якоря. Настройки карты приезжают новым объектом на каждом
 * перечитывании проекта — без отпечатка данные всех слоёв указателей пересобирались бы впустую.
 */
export function wayfindingSignPreviewTypesKey(
  signTypes: readonly (WayfindingSignPreviewType & { id: string })[],
): string {
  return JSON.stringify(
    signTypes.map((type) => [
      type.id,
      type.sides,
      type.autocraftGeneratorHash ?? '',
      type.icon ?? '',
      type.previewAnchor ?? '',
      type.previewAnchorByPage ?? null,
    ]),
  );
}

/** Обратно из отпечатка — типы по id, равные исходным во всём, что касается превью. */
export function wayfindingSignPreviewTypesFromKey(
  key: string,
): Map<string, WayfindingSignPreviewType> {
  const entries = JSON.parse(key) as Array<
    [string, number, string, string, string, Record<string, string> | null]
  >;
  return new Map(
    entries.map(([id, sides, generator, icon, anchor, anchorByPage]) => [
      id,
      {
        sides,
        ...(generator ? { autocraftGeneratorHash: generator } : {}),
        ...(icon ? { icon } : {}),
        ...(anchor ? { previewAnchor: anchor } : {}),
        ...(anchorByPage ? { previewAnchorByPage: anchorByPage } : {}),
      },
    ]),
  );
}
